// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Video from "@mcmc/App/Components/YoutubeVideo";
import React from "react";
import styled, { css } from "styled-components";

export const enum fields {
    src = "src",
    fullWidth = "fullWidth",
    autoPlay = "autoPlay",
    title = "title",
    description = "description",
}
interface VideoWidgetProps extends WidgetProps {
    fields: {
        [fields.src]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.autoPlay]: boolean;
        [fields.fullWidth]: boolean;
    };
}
type Props = VideoWidgetProps;
interface VideoWidgetStyles {
    container?: InjectableCss;
    videoContainer?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}
export const videoWidgetStyles: VideoWidgetStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            flex-wrap: wrap;
            margin: -15px;
        `,
    },
    videoContainer: {
        css: css`
            overflow: hidden;
            padding-top: 56.25%;
            position: relative;
            iframe {
                border: 0;
                height: 100%;
                left: 0;
                position: absolute;
                top: 0;
                width: 100%;
            }
        `,
    },
    rightSection: {
        css: css`
            flex: 1 1 400px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 15px;
        `,
    },
    leftSection: {
        css: css`
            padding: 15px;
            flex: 1 1 400px;
        `,
    },
    title: { variant: "h2" },
    description: { variant: "h5", weight: 400, transform: "none" },
};

const styles = videoWidgetStyles;

const VideoWidget: React.FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftSection}>
                <StyledWrapper {...styles.videoContainer}>
                    <Video src={fields.src} autoPlay={fields.autoPlay} />
                </StyledWrapper>
            </StyledWrapper>
            {!fields.fullWidth && (
                <StyledWrapper {...styles.rightSection}>
                    <Typography {...styles.title}>{fields.title}</Typography>
                    <Typography {...styles.description}>{fields.description}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            displayName: "Video Src",
            name: fields.src,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            displayName: "Is Full Width",
            name: fields.fullWidth,
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            dependentFields: [fields.title, fields.description],
        },
        {
            displayName: "Auto Play",
            name: fields.autoPlay,
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            displayName: "Video Title",
            name: fields.title,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
            isVisible: item => {
                return !item.fields.fullWidth;
            },
        },
        {
            displayName: "Video Description",
            name: fields.description,
            fieldType: "Contextual",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            isRequired: true,
            isVisible: item => {
                return !item.fields.fullWidth;
            },
        },
    ],
};
const widgetModule: WidgetModule = {
    component: VideoWidget,
    definition,
};
export default widgetModule;
